import { Box, Flex, Link, Text } from '@chakra-ui/react';
import * as m from 'motion/react-m';

export type FaqAccordionItem = {
  key: string;
  question: string;
  answer: string;
  link?: { label: string; href: string };
  /** DOM id for deep-linking to this question (e.g. `/faq#faq.voice.who`). */
  anchorId?: string;
};

type FaqAccordionRowProps = {
  item: FaqAccordionItem;
  isOpen: boolean;
  onToggle: () => void;
  delay: number;
};

const FaqAccordionRow = ({ item, isOpen, onToggle, delay }: FaqAccordionRowProps) => (
  <Box
    id={item.anchorId}
    scrollMarginTop="96px"
    border="1px solid"
    borderColor={isOpen ? 'whiteAlpha.200' : 'whiteAlpha.100'}
    borderRadius="xl"
    overflow="hidden"
    transition="border-color 0.2s"
    asChild
  >
    <m.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.4 }}
    >
      <Flex
        asChild
        w="100%"
        px={6}
        py={5}
        alignItems="center"
        justifyContent="space-between"
        cursor="pointer"
        bg={isOpen ? 'whiteAlpha.50' : 'transparent'}
        _hover={{ bg: 'whiteAlpha.50' }}
        transition="background 0.2s"
        textAlign="left"
      >
        <button type="button" onClick={onToggle}>
          <Text as="span" fontWeight="700" textStyle={{ base: 'md', md: 'lg' }} pr={4}>
            {item.question}
          </Text>
          <Text
            as="span"
            textStyle="xl"
            color="purple.300"
            flexShrink={0}
            transition="transform 0.2s"
            style={{ transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}
          >
            +
          </Text>
        </button>
      </Flex>
      {isOpen && (
        <Box px={6} pb={5}>
          <Text textStyle="sm" color="fg.muted" lineHeight="1.8" whiteSpace="pre-line">
            {item.answer}
          </Text>
          {item.link && (
            <Link
              href={item.link.href}
              mt={3}
              display="inline-block"
              color="purple.300"
              textStyle="title"
              _hover={{ color: 'purple.200', textDecoration: 'underline' }}
            >
              {item.link.label} →
            </Link>
          )}
        </Box>
      )}
    </m.div>
  </Box>
);

type FaqAccordionProps = {
  items: FaqAccordionItem[];
  /** Key of the currently open item (`null` = all closed). Controlled by the parent. */
  openKey: string | null;
  onToggle: (key: string) => void;
};

export const FaqAccordion = ({ items, openKey, onToggle }: FaqAccordionProps) => (
  <Flex direction="column" gap={3} w="100%">
    {items.map((item, index) => (
      <FaqAccordionRow
        key={item.key}
        item={item}
        isOpen={openKey === item.key}
        onToggle={() => onToggle(item.key)}
        delay={index * 0.05}
      />
    ))}
  </Flex>
);
